import {
  apiRequestGet,
  apiRequestPost,
  apiRequestPut,
  apiRequestPatch,
  apiRequestDelete,
} from 'mastodon/api';

import type {
  ApiGroupJSON,
  ApiGroupCategoryJSON,
  ApiGroupMembershipJSON,
  ApiGroupMembershipRole,
  ApiGroupBanJSON,
  ApiGroupMembershipRequestJSON,
  ApiGroupInviteJSON,
  ApiGroupFeedJSON,
  ApiGroupFeaturedAccountJSON,
  ApiGroupPostFilterJSON,
  ApiGroupPostFilterKind,
  ApiCreateGroupPayload,
  ApiUpdateGroupPayload,
} from '../api_types/groups';
import type { ApiStatusJSON } from '../api_types/statuses';

const buildGroupFormData = (
  payload: Partial<ApiCreateGroupPayload>,
): FormData | Partial<ApiCreateGroupPayload> => {
  if (!payload.avatar && !payload.cover_image) {
    return payload;
  }

  const formData = new FormData();
  Object.entries(payload).forEach(([key, value]: [string, unknown]) => {
    if (value !== undefined && value !== null) {
      formData.append(key, value as string | Blob);
    }
  });
  return formData;
};

export const apiGetGroups = (params?: {
  category_id?: string;
  membership?: 'member' | 'admin';
  max_id?: string;
}) => apiRequestGet<ApiGroupJSON[]>('v1/groups', params);

export const apiGetSuggestedGroups = () =>
  apiRequestGet<ApiGroupJSON[]>('v1/groups/suggestions');

export const apiGetGroup = (id: string) =>
  apiRequestGet<ApiGroupJSON>(`v1/groups/${id}`);

export const apiCreateGroup = (payload: ApiCreateGroupPayload) =>
  apiRequestPost<ApiGroupJSON>('v1/groups', buildGroupFormData(payload));

export const apiUpdateGroup = ({ id, ...payload }: ApiUpdateGroupPayload) =>
  apiRequestPut<ApiGroupJSON>(`v1/groups/${id}`, buildGroupFormData(payload));

export const apiDeleteGroup = (id: string) =>
  apiRequestDelete(`v1/groups/${id}`);

export const apiGetGroupCategories = () =>
  apiRequestGet<ApiGroupCategoryJSON[]>('v1/group_categories');

// For a public group, resolves to the new ApiGroupMembershipJSON. For a
// private group, joining creates a pending ApiGroupMembershipRequestJSON
// instead (no `role` field -- callers should branch on that to tell the two
// apart).
export const apiJoinGroup = (groupId: string) =>
  apiRequestPost<ApiGroupMembershipJSON | ApiGroupMembershipRequestJSON>(
    `v1/groups/${groupId}/membership`,
  );

export const apiLeaveGroup = (groupId: string) =>
  apiRequestDelete(`v1/groups/${groupId}/membership`);

export const apiMuteGroup = (groupId: string) =>
  apiRequestPost<ApiGroupMembershipJSON>(`v1/groups/${groupId}/mute`);

export const apiUnmuteGroup = (groupId: string) =>
  apiRequestDelete(`v1/groups/${groupId}/mute`);

export const apiGetGroupMembers = (
  groupId: string,
  params?: { max_id?: string },
) =>
  apiRequestGet<ApiGroupMembershipJSON[]>(
    `v1/groups/${groupId}/memberships`,
    params,
  );

export const apiRemoveGroupMember = (groupId: string, accountId: string) =>
  apiRequestDelete(`v1/groups/${groupId}/memberships/${accountId}`);

export const apiUpdateGroupMemberRole = (
  groupId: string,
  accountId: string,
  role: ApiGroupMembershipRole,
) =>
  apiRequestPatch<ApiGroupMembershipJSON>(
    `v1/groups/${groupId}/memberships/${accountId}`,
    { role },
  );

export const apiReorderGroupPins = (groupId: string, ids: string[]) =>
  apiRequestPost<ApiStatusJSON[]>(`v1/groups/${groupId}/pins/reorder`, {
    ids,
  });

export const apiTransferGroupOwnership = (
  groupId: string,
  newOwnerAccountId: string,
) =>
  apiRequestPost<ApiGroupJSON>(`v1/groups/${groupId}/transfer_ownership`, {
    new_owner_account_id: newOwnerAccountId,
  });

export const apiGetGroupBans = (
  groupId: string,
  params?: { max_id?: string },
) => apiRequestGet<ApiGroupBanJSON[]>(`v1/groups/${groupId}/bans`, params);

export const apiBanGroupMember = (
  groupId: string,
  accountId: string,
  reason?: string,
) =>
  apiRequestPost<ApiGroupBanJSON>(`v1/groups/${groupId}/bans`, {
    account_id: accountId,
    reason,
  });

export const apiUnbanGroupAccount = (groupId: string, accountId: string) =>
  apiRequestDelete(`v1/groups/${groupId}/bans/${accountId}`);

export const apiGetGroupMembershipRequests = (
  groupId: string,
  params?: { max_id?: string },
) =>
  apiRequestGet<ApiGroupMembershipRequestJSON[]>(
    `v1/groups/${groupId}/membership_requests`,
    params,
  );

export const apiAuthorizeGroupMembershipRequest = (
  groupId: string,
  requestId: string,
) =>
  apiRequestPost<ApiGroupMembershipJSON>(
    `v1/groups/${groupId}/membership_requests/${requestId}/authorize`,
  );

export const apiRejectGroupMembershipRequest = (
  groupId: string,
  requestId: string,
) =>
  apiRequestPost(
    `v1/groups/${groupId}/membership_requests/${requestId}/reject`,
  );

export const apiGetGroupInvites = (
  groupId: string,
  params?: { max_id?: string },
) =>
  apiRequestGet<ApiGroupInviteJSON[]>(`v1/groups/${groupId}/invites`, params);

export const apiCreateGroupInvite = (groupId: string, accountId: string) =>
  apiRequestPost<ApiGroupInviteJSON>(`v1/groups/${groupId}/invites`, {
    account_id: accountId,
  });

export const apiRevokeGroupInvite = (groupId: string, accountId: string) =>
  apiRequestDelete(`v1/groups/${groupId}/invites/${accountId}`);

export const apiGetMyGroupInvites = (params?: { max_id?: string }) =>
  apiRequestGet<ApiGroupInviteJSON[]>('v1/group_invites', params);

export const apiAcceptGroupInvite = (inviteId: string) =>
  apiRequestPost<ApiGroupMembershipJSON>(`v1/group_invites/${inviteId}/accept`);

export const apiRejectGroupInvite = (inviteId: string) =>
  apiRequestPost(`v1/group_invites/${inviteId}/reject`);

export const apiGetGroupFeeds = (groupId: string) =>
  apiRequestGet<ApiGroupFeedJSON[]>(`v1/groups/${groupId}/feeds`);

export const apiCreateGroupFeed = (
  groupId: string,
  feedUrl: string,
  sourceName?: string,
) =>
  apiRequestPost<ApiGroupFeedJSON>(`v1/groups/${groupId}/feeds`, {
    group_feed: { feed_url: feedUrl, source_name: sourceName },
  });

export const apiDeleteGroupFeed = (groupId: string, feedId: string) =>
  apiRequestDelete(`v1/groups/${groupId}/feeds/${feedId}`);

export const apiRetryGroupFeed = (groupId: string, feedId: string) =>
  apiRequestPost<ApiGroupFeedJSON>(
    `v1/groups/${groupId}/feeds/${feedId}/retry`,
  );

export const apiGetGroupFeaturedAccounts = (groupId: string) =>
  apiRequestGet<ApiGroupFeaturedAccountJSON[]>(
    `v1/groups/${groupId}/featured_accounts`,
  );

export const apiCreateGroupFeaturedAccount = (groupId: string, acct: string) =>
  apiRequestPost<ApiGroupFeaturedAccountJSON>(
    `v1/groups/${groupId}/featured_accounts`,
    { acct },
  );

export const apiDeleteGroupFeaturedAccount = (
  groupId: string,
  featuredAccountId: string,
) =>
  apiRequestDelete(
    `v1/groups/${groupId}/featured_accounts/${featuredAccountId}`,
  );

export const apiGetGroupPostFilters = (groupId: string) =>
  apiRequestGet<ApiGroupPostFilterJSON[]>(`v1/groups/${groupId}/post_filters`);

export const apiCreateGroupPostFilter = (
  groupId: string,
  kind: ApiGroupPostFilterKind,
  value: string,
) =>
  apiRequestPost<ApiGroupPostFilterJSON>(`v1/groups/${groupId}/post_filters`, {
    group_post_filter: { kind, value },
  });

export const apiDeleteGroupPostFilter = (groupId: string, filterId: string) =>
  apiRequestDelete(`v1/groups/${groupId}/post_filters/${filterId}`);
